import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Card, ToggleButton, ToggleButtonGroup } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { toSalePricingMode } from '../manualImportMeta';
import type { ManualImportForm } from '../useManualImport';
import { ProductLineRow } from './ProductLineRow';
import { ProductPicker } from './ProductPicker';

type ProductLinesSectionProps = {
    form: ManualImportForm;
};

/** The product lines card: add-existing / add-new actions and the line list. */
export const ProductLinesSection: React.FC<ProductLinesSectionProps> = ({
    form,
}) => {
    const { t } = useTranslation('pages');

    return (
        <section {...getNodeTestIdProp('manual-import-lines')}>
            <Card {...getNodeTestIdProp('manual-import-lines-card')}>
                <Card.Header
                    className="flex flex-row flex-wrap items-center justify-between gap-3"
                    {...getNodeTestIdProp('manual-import-lines-header')}
                >
                    <Card.Title
                        {...getNodeTestIdProp('manual-import-lines-title')}
                    >
                        {t('purchases.manualImport.products')}
                    </Card.Title>
                    <div
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('manual-import-lines-actions')}
                    >
                        <ToggleButtonGroup
                            aria-label={t(
                                'purchases.manualImport.pricingModeAria',
                            )}
                            disallowEmptySelection
                            onSelectionChange={(keys) =>
                                form.onPricingModeChange(
                                    toSalePricingMode(keys),
                                )
                            }
                            selectedKeys={[form.state.pricingMode]}
                            selectionMode="single"
                            {...getNodeTestIdProp('manual-import-pricing-mode')}
                        >
                            <ToggleButton
                                {...getNodeTestIdProp(
                                    'manual-import-pricing-mode-fixed',
                                )}
                                id="fixed"
                                size="sm"
                            >
                                {t('purchases.manualImport.pricingModeFixed')}
                            </ToggleButton>
                            <ToggleButton
                                {...getNodeTestIdProp(
                                    'manual-import-pricing-mode-markup',
                                )}
                                id="markup"
                                size="sm"
                            >
                                {t('purchases.manualImport.pricingModeMarkup')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                        <ProductPicker onPick={form.onAddExistingLine} />
                        <Button
                            onPress={form.onAddNewLine}
                            size="sm"
                            variant="tertiary"
                            {...getNodeTestIdProp(
                                'manual-import-add-new-product',
                            )}
                        >
                            <Icon icon="gravity-ui:plus" />
                            {t('purchases.manualImport.addNewProduct')}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Content
                    {...getNodeTestIdProp('manual-import-lines-content')}
                >
                    {form.state.lines.length === 0 ? (
                        <p
                            className="py-6 text-center text-sm text-muted"
                            {...getNodeTestIdProp('manual-import-lines-empty')}
                        >
                            {t('purchases.manualImport.noProducts')}
                        </p>
                    ) : (
                        <div
                            className="flex flex-col gap-3"
                            {...getNodeTestIdProp('manual-import-lines-list')}
                        >
                            {form.state.lines.map((line) => (
                                <ProductLineRow
                                    form={form}
                                    key={line.key}
                                    line={line}
                                />
                            ))}
                        </div>
                    )}
                </Card.Content>
            </Card>
        </section>
    );
};
